<template>
  <div class="right-column-settings">
    <div class="settings-content__wrapper">
      <div class="horizontal layout center">
        <aha-icon
          name="award"
          class="settings-icon"
        ></aha-icon>
        <span class="px-2 font-normal">{{ $i18nPresenter.t('Points') }}</span>
        <span class="title-table-presentation-tooltip result-layout-tooltip">
          <b-button
            v-tooltip="$i18nPresenter.t('Choose the points range for this question.')"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </div>
      <QuizPointConfig />
    </div>
    <div class="settings-content__wrapper sub-content">
      <div class="horizontal layout center">
        <span class="pr-2 font-normal">
          {{ $i18nPresenter.t('Faster answers get more points') }}
          <span class="title-table-presentation-tooltip result-layout-tooltip">
            <b-button
              v-tooltip="fasterAnswerTooltip"
              class="tooltip-btn"
            />
            <i class="fas fa-question-circle" />
          </span>
        </span>
      </div>
      <QuizFasterAnswerGetMorePointToggle />
    </div>
    <div
      v-if="isShowPartialCorrectSetting"
      class="settings-content__wrapper sub-content"
      :class="{
        'over': isShowPartiallyCorrectTooltip
      }"
    >
      <div class="horizontal layout center">
        <span class="pr-2 font-normal">{{ $i18nPresenter.t('Partial scoring') }}</span>
        <span class="title-table-presentation-tooltip result-layout-tooltip">
          <b-button
            v-tooltip="{
              content: partiallyCorrectTooltip,
              show: isShowPartiallyCorrectTooltip
            }"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </div>
      <PartialScoringToggle />
    </div>
    <div class="settings-content__wrapper">
      <div class="horizontal layout center">
        <aha-icon
          name="stopwatch"
          class="settings-icon mb-0.5"
        ></aha-icon>
        <span class="px-2 font-normal">{{ $i18nPresenter.t('Time limit') }}</span>
        <span class="title-table-presentation-tooltip result-layout-tooltip">
          <b-button
            v-tooltip="timeLimitTooltip"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </div>
      <TimeToAnswerConfig />
    </div>
    <div class="settings-content__wrapper">
      <div class="horizontal layout center">
        <aha-icon
          name="trophy"
          class="settings-icon"
        ></aha-icon>
        <span class="px-2 font-normal">{{ $i18nPresenter.t('Leaderboard') }}</span>
        <span class="title-table-presentation-tooltip result-layout-tooltip">
          <b-button
            v-tooltip="$i18nPresenter.t('Show a leaderboard slide after this question.')"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </div>
      <QuizLeaderboardToggle />
    </div>
    <div class="settings-content__wrapper general-quiz-settings" @click="openQuizSettings()">
      <div class="horizontal layout center">
        <aha-icon
          name="setting"
          class="settings-icon"
        ></aha-icon>
        <span class="px-2 font-normal">{{ $i18nPresenter.t('General quiz settings') }}</span>
      </div>
      <aha-icon name="arrow-no-tail" class="settings-icon__arrow" />
    </div>
  </div>
</template>

<script>
import TimeToAnswerConfig from '@/components-v2/presentation-editor/side-right/TimeToAnswerConfig.vue'
import QuizPointConfig from '@/components-v2/presentation-editor/side-right/QuizPointConfig.vue'
import QuizFasterAnswerGetMorePointToggle from '@/components-v2/presentation-editor/side-right/QuizFasterAnswerGetMorePointToggle.vue'
import QuizLeaderboardToggle from '@/components-v2/presentation-editor/side-right/QuizLeaderboardToggle.vue'
import PartialScoringToggle from '@/components-v2/presentation-editor/side-right/PartialScoringToggle.vue'

import EventBus from '@/eventBus'

export default {
  components: {
    TimeToAnswerConfig,
    QuizPointConfig,
    QuizFasterAnswerGetMorePointToggle,
    PartialScoringToggle,
    QuizLeaderboardToggle,
  },
  props: {
    slideType: {
      type: String,
      default: null,
    },
    multipleChoice: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isShowPartiallyCorrectTooltip: false,
    }
  },
  computed: {
    fasterAnswerTooltip() {
      return this.$i18nPresenter.t('Players score between the max and min points, depending on how quickly they answer correctly.')
    },
    timeLimitTooltip() {
      return this.$i18nPresenter.t('Choose the maximum time in which players can answer')
    },
    partiallyCorrectTooltip() {
      if (this.slideType === 'matchPairs') {
        return this.$i18nPresenter.t("Players score points for each correct answer, even if they don't get all answers correct.")
      }

      return this.$i18nPresenter.t('Players score points for each correct answer, but selecting any incorrect answer will result in 0 points.')
    },
    isShowPartialCorrectSetting() {
      return this.slideType === 'matchPairs' || this.multipleChoice
    },
  },
  watch: {
    isShowPartialCorrectSetting: {
      handler(value, oldValue) {
        if (oldValue === undefined) return

        this.isShowPartiallyCorrectTooltip = value

        if (value) {
          setTimeout(() => {
            this.isShowPartiallyCorrectTooltip = false
          }, 4000)
        }
      },
      immediate: true,
    },
  },
  methods: {
    openQuizSettings() {
      EventBus.$emit('show-setting-popup', { defaultExpandedAccordion: 'quiz' })
      this.$store.state.activeModelDetailSlide = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss-v2/variables.scss";
.right-column-settings {
  z-index: 9; // https://ahaslides.atlassian.net/browse/AHA-9036
  position: relative;
  & ::v-deep {
    .accordion-item:hover {
      .accordion-item-icon {
        color: inherit;
      }
    }
    .accordion-item-icon {
      color: $secondary-20-color;
    }
    .accordion-item-title-content {
      justify-content: space-between;
      padding: 1rem 0;
    }
  }
  .settings-content__wrapper {
    @apply flex items-center justify-between;
    @apply py-4;
    @apply border-t border-base-30;
    padding-left: 2px;
    &:hover, &.over {
      .title-table-presentation-tooltip {
        opacity: 1;
      }
      &.general-quiz-settings {
        @apply text-primary-100;
        .settings-icon, .settings-icon__arrow {
          @apply text-primary-100;
        }
      }
    }
    &.sub-content {
      margin-left: 1.75rem;
      border-top: none;
      padding-top: 0;
    }
    &.general-quiz-settings {
      @apply cursor-pointer;
      .settings-icon__arrow {
        @apply text-secondary-40;
      }
    }
    .settings-icon {
      color: $secondary-60-color;
    }
    .settings-content__link {
      color: inherit;
      text-decoration: underline;
    }
    .title-table-presentation-tooltip {
      opacity: 0;
      transition: all 0.2s ease-in-out;
    }
  }
}
</style>
